/**
 * Typen und Hilfen der Seite „Sprachalarmanlage" (W2-D2, 16.10.2026; seit SA-1 vom 20.10.2026 mit Heft
 * ELT-SAH, Kapitelwahl, Vorbehalten, Annahmen, Befunden, Fachfragen und Lautsprecherliste).
 *
 * Die Typen folgen `GET …/elektro/sicherheitstechnik/saa` (SicherheitstechnikEndpoints.cs, Saastand.cs,
 * SaaInhalt.cs, Saalauf.cs, Saanachweis.cs). Gerechnet wird beim Abruf; die Seite schreibt nichts. Vorbehalte,
 * Annahmen und Fachfragen kommen vom Server im selben Wortlaut wie im Heft (TX-STANDARD S0.5).
 */
import type { BmaAnnahme, BmaBefund, BmaFachfrage, BmaKapitel, BmaVorbehalt } from './bma';
import { zeitpunkt } from './bma';
import type { StNotstrom, StVorgabe } from './sicherheitstechnik';

export interface SaaKennzahlen {
  zentralen: number; linien: number; lautsprecher: number; leistungW: number; abschnitte: number;
  leistungAusVorgabe: number; leistungAusName: number; leistungGepflegt: number;
  linienUeberSpannungsfall: number; linienStraengeGemischt: number;
  befunde: number; warnungen: number; fehler: number; aufAnnahmen: boolean;
}

export interface SaaLinie {
  linie: string; zentrale: string; lautsprecher: number; leistungW: number; stromA: number;
  laengeM: number | null; leitungsart: string | null; querschnittMm2: number | null;
  querschnittHerleitung: string | null; schleifenwiderstandOhm: number | null;
  spannungsfallV: number | null; spannungsfallProzent: number | null; spannungEndeV: number | null;
  pegelverlustDb: number | null; zulaessigProzent: number; laengeMaxM: number | null;
  querschnittErforderlichMm2: number | null; querschnittVorschlagMm2: number | null;
  kanalauslastungProzent: number | null; strangA: number; strangB: number; strangAb: number;
  strangOhne: number; abschnitte: string[]; funktionserhalt: string | null; gegenAnnahmen: boolean;
  status: string; befunde: string[]; rechenweg: string | null;
  /** SA-2a: ΔU mit allen Lautsprechern am Linienende (k = 1,0) - die Empfindlichkeit. */
  spannungsfallProzentLastEnde?: number | null;
  /** SA-2a: Teile der oberen Schranke bei gleicher Länge (Rechenwert, kein Planungsvorschlag). */
  teileObereSchranke?: number | null;
  teileObereSchrankeLastEnde?: number | null;
}

/** SA-2a (Entscheidung main Z1): wortgleich mit Saanachweis.TeilungHinweis. */
export const TEILUNG_HINWEIS = 'Rechenwert, obere Schranke bei gleicher Länge, kein Planungsvorschlag';

export interface SaaKanal { nummer: number; leistungW: number; linien: string[] }

export interface SaaVerstaerker {
  zentrale: string; linien: number; leistungW: number; leistungGleichzeitigW: number;
  kanalleistungW: number | null; kanalNutzbarW: number | null; kanaeleJeEinheit: number | null;
  kanaeleErforderlich: number | null; einheitenErforderlich: number | null;
  einheitenMitErsatz: number | null; einheitenVerbaut: number; einheitBezeichnung: string | null;
  leistungVerbautW: number | null; kreiseMax: number | null; kanalbelegung: SaaKanal[];
  gegenAnnahmen: boolean; status: string; befunde: string[]; rechenweg: string[];
}

/**
 * SA-3: eine Lautsprecherzone - `name` ist das gepflegte Feld „beschallungszone“ oder der Abschnitt aus Quadrant und
 * Geschoss, `herkunft` sagt welches, `abschnitte` sind die Abschnitte ihrer Lautsprecher.
 */
export interface SaaZone {
  name: string; herkunft: string; abschnitte: string[]; lautsprecher: number; leistungW: number; linien: string[];
  strangA: number; strangB: number; strangAb: number; strangOhne: number; befunde: string[];
}

/** Die Festlegung der Alarmierung, wörtlich wie im Heft (SA-3; seit F78, Eigentümer 27.10.2026, kein Vorbehalt mehr). */
export const FESTLEGUNG_ALARMIERUNG = 'Vollevakuierung (SAA-04, Eigentümer 27.10.2026)';

export interface SaaNotstromteile {
  zentrale: string; systemspannungV: number; zentraleRuheA: number; zentraleAlarmA: number;
  verstaerkereinheiten: number; davonErsatz: number; verstaerkerRuheJeEinheitW: number; verstaerkerRuheA: number;
  lautsprecherleistungW: number; gleichzeitigkeit: number; alarmleistungFaktor: number; wirkungsgrad: number;
  endstufenAlarmW: number; endstufenAlarmA: number; ruhestromA: number; alarmstromA: number;
  ruheleistungW: number; alarmleistungW: number; ruhezeitH: number; alarmzeitH: number;
  ruheAh: number; alarmAh: number; zuschlag: number; kapazitaetAh: number; ruheanteilProzent: number;
  energieKWh: number; vergleich: { ruhezeitH: number; kapazitaetAh: number }[];
}

/** SA-1: die Stände, aus denen Seite, Heft und Listen lesen. */
export interface SaaStaende {
  linien: string | null; bauteile: string | null; bestueckung: string | null; steuermatrix: string;
}

/** SA-1: Kennzahlen des Stands (Befunde aus Saastand, mit den beiden neuen Befunden zu Zonen und Funktionserhalt). */
export interface SaaUebersicht {
  zentralen: number; linien: number; lautsprecher: number; leistungW: number; zonen: number; vorgaben: number;
  vorgabenUnbestaetigt: number; fehler: number; warnungen: number; hinweise: number;
}

export interface SaaZentrale {
  kennzeichen: string; bezeichnung: string | null; hersteller: string | null; baureihe: string | null;
  raum: string | null; geschoss: string | null; quadranten: string | null; linien: number; lautsprecher: number;
  leistungW: number; kreiseMax: number | null; kanalleistungW: number | null; kanalHerkunft: string;
  einspeisung: boolean;
}

export interface SaaLautsprecher {
  zentrale: string; linie: string; nummer: string | null; bauteilart: string; familie: string | null;
  typ: string | null; strang: string | null; leistungW: number; leistungHerkunft: string; raum: string | null;
  raumname: string | null; geschoss: string | null; quadrant: string | null; abschnitt: string;
}

export interface SaaStand {
  stand: string;
  staende: SaaStaende;
  rechenstand: string;
  kennzahlen: SaaKennzahlen;
  uebersicht: SaaUebersicht;
  vorbehalte: BmaVorbehalt[];
  kapitel: BmaKapitel[];
  zentralen: SaaZentrale[];
  lautsprecher: SaaLautsprecher[];
  linien: SaaLinie[];
  verstaerker: SaaVerstaerker[];
  notstrom: StNotstrom[];
  notstromTeile: SaaNotstromteile[];
  zonen: SaaZone[];
  vorgaben: StVorgabe[];
  annahmen: BmaAnnahme[];
  befunde: BmaBefund[];
  fachfragen: BmaFachfrage[];
  rho: number;
  spannungV: number;
}

/** Der Rechenstand im Seitenkopf (S1.2): die Stände, aus denen die Seite liest. */
export function saaRechenstand(s: SaaStaende): string {
  return `Linien ${zeitpunkt(s.linien)} · Lautsprecher gelesen ${zeitpunkt(s.bauteile)} · Bestückung ${zeitpunkt(s.bestueckung)}`;
}

/**
 * Was ein einzelnes Kapitel des Hefts ELT-SAH als PDF liefert: die eigene Berichtsart (ELT-SPA, ELT-LSL), sonst ein
 * Teilheft ELT-SAH mit diesem Kapitel und den Pflichtkapiteln.
 */
export function saaEinzelausgabe(k: BmaKapitel): 'spa' | 'lsl' | 'teilheft' | null {
  if (k.art !== 'wahl') return null;
  switch (k.einzelausgabe) {
    case 'ELT-SPA': return 'spa';
    case 'ELT-LSL': return 'lsl';
    default: return 'teilheft';
  }
}
